// NEW_FILE_JUSTIFIED: Three-column type builder page assembling Toolbox/Canvas/Inspector + History tab
import { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the standard Convex React client package, not a generated file
import { api } from '@convex/_generated/api';
import { ArrowLeft, Save, Eye, Rows2, Rows3 } from 'lucide-react';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useTypeBuilder } from '@/hooks/useTypeBuilder';
import { useBuilderHeaderState } from '@/hooks/useBuilderHeaderState';
import { Toolbox } from '@/components/insurance/builder/Toolbox';
import { Canvas } from '@/components/insurance/builder/Canvas';
import { Inspector } from '@/components/insurance/builder/Inspector';
import { HistoryTab } from '@/components/insurance/builder/HistoryTab';
import { TypeWizardStepper } from '@/components/insurance/types/Wizard/Stepper';
import { SectionedPolicyForm } from '@/components/insurance/policy-application/SectionedPolicyForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import type { Id } from '@convex/_generated/dataModel';

export function TypeBuilderPage() {
  const { typeId } = useParams<{ typeId: string }>();
  const navigate = useNavigate();
  const { currentOrg } = useOrgContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const typedId = typeId as Id<'orgInsuranceTypes'> | undefined; // AS_JUSTIFIED: one-time Id cast from route param consolidated at component top

  const type = useQuery(
    api['insurance/types'].getByIdWithSections,
    typedId ? { typeId: typedId } : 'skip'
  );

  const upsert = useMutation(api['insurance/types'].upsertWithSections);
  const { state, dispatch, init } = useTypeBuilder();
  const { density, previewOpen, toggleDensity, setPreviewOpen } = useBuilderHeaderState();

  const fromAi = searchParams.get('aiExtracted') === '1';

  useEffect(() => {
    if (!type || fromAi) return;
    init({
      meta: {
        providerId: type.providerId,
        name: type.name,
        supportsDrivers: type.supportsDrivers ?? false,
      },
      sections: type.sections,
    });
  }, [type, init, fromAi]);

  useEffect(() => {
    if (!type || !fromAi) return;
    dispatch({
      type: 'INIT_FROM_AI',
      state: {
        meta: {
          providerId: type.providerId,
          name: type.name,
          supportsDrivers: type.supportsDrivers ?? false,
        },
        sections: type.sections,
      },
      aiKeys: {
        sectionKeys: type.sections.map((s: { sectionKey: string }) => s.sectionKey),
        fieldKeys: type.sections.flatMap((s: { fields: { fieldKey: string }[] }) =>
          s.fields.map((f: { fieldKey: string }) => f.fieldKey)
        ),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type?._id, fromAi]);

  if (!typedId || !currentOrg) return null;
  if (type === undefined) {
    return (
      <div className="p-8 space-y-4" aria-busy="true" aria-label="Loading type builder">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (type === null) {
    return (
      <div className="p-8">
        <p className="text-muted-foreground">Type not found.</p>
      </div>
    );
  }

  const stepperSteps = [
    { key: 'source' as const, label: 'Source' },
    ...(type.aiExtraction ? [{ key: 'extract' as const, label: 'Extract' }] : []),
    { key: 'builder' as const, label: 'Builder' },
    { key: 'review' as const, label: 'Review' },
  ];
  const currentStepIndex = stepperSteps.findIndex(s => s.key === 'builder');
  const lastSectionKey = state.sections[state.sections.length - 1]?.sectionKey ?? null;

  async function handleSave() {
    if (!currentOrg || !typedId) return;
    try {
      await upsert({
        typeId: typedId,
        orgId: currentOrg._id,
        providerId: state.meta.providerId as Id<'insuranceProviders'> | undefined, // AS_JUSTIFIED: meta carries Id<'insuranceProviders'> from initial load via getByIdWithSections
        name: state.meta.name,
        supportsDrivers: state.meta.supportsDrivers,
        sections: state.sections,
      });
      dispatch({ type: 'MARK_SAVED' });
      if (fromAi) {
        searchParams.delete('aiExtracted');
        setSearchParams(searchParams, { replace: true });
      }
      toast.success('Saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    }
  }

  return (
    <div className="min-h-full bg-background-secondary flex flex-col">
      <div className="border-b bg-card px-6 py-3">
        <TypeWizardStepper steps={stepperSteps} currentIndex={currentStepIndex} />
      </div>
      <header className="px-6 py-3 border-b bg-card flex items-center gap-3 flex-shrink-0">
        <Button variant="ghost" size="icon" onClick={() => navigate('/insurance/types')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <Input
          value={state.meta.name}
          onChange={e => dispatch({ type: 'SET_META', patch: { name: e.target.value } })}
          className="max-w-md font-semibold h-9"
        />
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            aria-label={
              density === 'compact' ? 'Switch to cozy density' : 'Switch to compact density'
            }
            onClick={toggleDensity}
          >
            {density === 'compact' ? (
              <Rows2 className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Rows3 className="h-4 w-4" aria-hidden="true" />
            )}
            <span className="ml-1">{density === 'compact' ? 'Compact' : 'Cozy'}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
            <Eye className="h-4 w-4 mr-1" aria-hidden="true" />
            Preview
          </Button>
          <Button onClick={() => void handleSave()} disabled={!state.isDirty}>
            <Save className="w-4 h-4 mr-2" />
            {state.isDirty ? 'Save' : 'Saved'}
          </Button>
        </div>
      </header>
      <Tabs defaultValue="builder" className="flex-1 flex flex-col">
        <TabsList className="mx-6 mt-3 self-start">
          <TabsTrigger value="builder">Builder</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="builder" className="flex-1 grid grid-cols-[240px_1fr_320px] gap-0 m-0">
          <aside className="border-r bg-card overflow-hidden">
            <Toolbox
              hasSections={state.sections.length > 0}
              selection={state.selection}
              lastSectionKey={lastSectionKey}
              dispatch={dispatch}
            />
          </aside>
          <main className="overflow-hidden">
            <Canvas
              sections={state.sections}
              selection={state.selection}
              dispatch={dispatch}
              aiKeys={state.aiKeys}
              density={density}
            />
          </main>
          <aside className="border-l bg-card overflow-hidden">
            <Inspector selection={state.selection} sections={state.sections} dispatch={dispatch} />
          </aside>
        </TabsContent>
        <TabsContent value="history" className="flex-1 px-6 py-4 overflow-auto">
          <HistoryTab typeId={typedId} />
        </TabsContent>
      </Tabs>
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-auto">
          <SheetHeader>
            <SheetTitle>Form preview</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <SectionedPolicyForm
              sections={state.sections}
              fieldValues={[]}
              values={{}}
              onChange={() => {}}
              disabled
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
